import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, XCircle, RefreshCw, Settings, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function AdminBinanceSettingsPage() {
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toggling, setToggling] = useState(false);

    const [status, setStatus] = useState({
        configured: false,
        tradingEnabled: false,
        connectionStatus: { success: false },
        hasApiKey: false,
        hasSecret: false
    });

    const [credentials, setCredentials] = useState({
        apiKey: '',
        apiSecret: ''
    });

    const [showCredentials, setShowCredentials] = useState(false);

    useEffect(() => {
        loadStatus();
    }, []);

    const loadStatus = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/binance/status');
            setStatus(response.data);
        } catch (error) {
            console.error('Load status error:', error);
            toast.error('Failed to load Binance status');
        } finally {
            setLoading(false);
        }
    };

    const testConnection = async () => {
        try {
            setTesting(true);
            const response = await api.post('/admin/binance/test-connection');
            if (response.data.success) {
                toast.success('✅ Connection successful!');
            } else {
                toast.error(`❌ Connection failed: ${response.data.error}`);
            }
            await loadStatus();
        } catch (error) {
            toast.error('Connection test failed');
        } finally {
            setTesting(false);
        }
    };

    const handleToggleTrading = async () => {
        try {
            setToggling(true);
            const newState = !status.tradingEnabled;

            const response = await api.post('/admin/binance/toggle-trading', {
                enabled: newState
            });

            if (response.data.success) {
                toast.success(response.data.message);
                await loadStatus();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to toggle trading');
        } finally {
            setToggling(false);
        }
    };

    const handleSaveCredentials = async () => {
        if (!credentials.apiKey || !credentials.apiSecret) {
            toast.error('Please enter both API key and secret');
            return;
        }

        try {
            setSaving(true);
            const response = await api.post('/admin/binance/credentials', credentials);

            if (response.data.success) {
                toast.success('✅ Credentials saved and verified!');
                setCredentials({ apiKey: '', apiSecret: '' });
                setShowCredentials(false);
                await loadStatus();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save credentials');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">Binance Settings</h1>
                <p className="text-gray-400">Configure Binance API integration and trading mode</p>
            </div>

            {/* Trading Mode Card */}
            <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <Settings className="w-6 h-6 text-blue-500" />
                        <h2 className="text-xl font-semibold text-white">Trading Mode</h2>
                    </div>
                    {status.tradingEnabled ? (
                        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                            Binance-Backed
                        </span>
                    ) : (
                        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium">
                            Internal P2P
                        </span>
                    )}
                </div>

                <div className="mb-6 p-4 bg-gray-900/50 rounded-lg">
                    <div className="flex items-start gap-3 mb-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                        <div>
                            <p className="text-white font-medium mb-2">How it works:</p>
                            <ul className="text-gray-400 text-sm space-y-1 ml-4">
                                <li><strong className="text-green-400">Enabled</strong>: Orders are executed on Binance (requires valid API keys)</li>
                                <li><strong className="text-yellow-400">Disabled</strong>: Orders are matched internally between users (P2P matching)</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleToggleTrading}
                    disabled={toggling || (!status.configured && !status.tradingEnabled)}
                    className={`w-full py-3 rounded-lg font-semibold transition-all ${status.tradingEnabled
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : status.configured
                            ? 'bg-green-500 hover:bg-green-600 text-white'
                            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        } disabled:opacity-50`}
                >
                    {toggling ? (
                        <span className="flex items-center justify-center gap-2">
                            <RefreshCw className="w-5 h-5 animate-spin" />
                            Switching...
                        </span>
                    ) : status.tradingEnabled ? (
                        '🔴 Switch to Internal P2P Mode'
                    ) : status.configured ? (
                        '🟢 Switch to Binance-Backed Mode'
                    ) : (
                        '⚠️ Configure credentials first'
                    )}
                </button>
            </div>

            {/* Connection Status Card */}
            <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-white">Connection Status</h2>
                    <button
                        onClick={testConnection}
                        disabled={testing || !status.configured}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                        <RefreshCw className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} />
                        Test Connection
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-900/50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            {status.hasApiKey ? (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-400" />
                            )}
                            <span className="text-gray-400 text-sm">API Key</span>
                        </div>
                        <p className="text-white font-medium">
                            {status.hasApiKey ? 'Configured' : 'Not Set'}
                        </p>
                    </div>

                    <div className="p-4 bg-gray-900/50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            {status.hasSecret ? (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-400" />
                            )}
                            <span className="text-gray-400 text-sm">API Secret</span>
                        </div>
                        <p className="text-white font-medium">
                            {status.hasSecret ? 'Configured' : 'Not Set'}
                        </p>
                    </div>

                    <div className="p-4 bg-gray-900/50 rounded-lg col-span-2">
                        <div className="flex items-center gap-2 mb-2">
                            {status.connectionStatus?.success ? (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-400" />
                            )}
                            <span className="text-gray-400 text-sm">Connection Test</span>
                        </div>
                        <p className="text-white font-medium">
                            {status.connectionStatus?.success
                                ? status.connectionStatus.message || 'Connected'
                                : status.connectionStatus?.error || 'Not tested'}
                        </p>
                    </div>
                </div>
            </div>

            {/* API Credentials Card */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <Shield className="w-6 h-6 text-purple-500" />
                        <h2 className="text-xl font-semibold text-white">API Credentials</h2>
                    </div>
                    {!showCredentials && (
                        <button
                            onClick={() => setShowCredentials(true)}
                            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-all"
                        >
                            {status.configured ? 'Update Credentials' : 'Add Credentials'}
                        </button>
                    )}
                </div>

                {showCredentials && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Binance API Key</label>
                            <input
                                type="text"
                                value={credentials.apiKey}
                                onChange={(e) => setCredentials({ ...credentials, apiKey: e.target.value })}
                                placeholder="Enter your Binance API key"
                                className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-purple-500"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Binance API Secret</label>
                            <input
                                type="password"
                                value={credentials.apiSecret}
                                onChange={(e) => setCredentials({ ...credentials, apiSecret: e.target.value })}
                                placeholder="Enter your Binance API secret"
                                className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-purple-500"
                            />
                        </div>

                        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                            <p className="text-yellow-400 text-sm">
                                <strong>Security Note:</strong> Credentials are encrypted and stored securely.
                                Make sure to enable IP restrictions on your Binance API key for additional security.
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleSaveCredentials}
                                disabled={saving}
                                className="flex-1 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                            >
                                {saving ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                        Verifying...
                                    </span>
                                ) : (
                                    'Save & Verify Credentials'
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setShowCredentials(false);
                                    setCredentials({ apiKey: '', apiSecret: '' });
                                }}
                                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
